import type { Message } from '../types';

interface MediaContentProps {
  message: Message;
  isVisitor: boolean;
}

const textStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const captionStyle: React.CSSProperties = {
  ...textStyle,
  marginTop: 6,
  fontSize: 14,
  lineHeight: 1.4,
};

/**
 * Renderiza o conteúdo de mídia de uma mensagem (image/video/audio/document).
 * Só a mídia — a "casca" do balão (cor/raio/hora/status) é responsabilidade de cada interface.
 * Sem mediaUrl → fallback pro texto (mesmo comportamento da hosted antiga).
 */
export function MediaContent({ message, isVisitor }: MediaContentProps) {
  const { messageType, mediaUrl, content } = message;

  if (!mediaUrl) {
    return <div style={textStyle}>{content}</div>;
  }

  if (messageType === 'image') {
    return (
      <a href={mediaUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
        <img
          src={mediaUrl}
          alt={content || 'imagem'}
          loading="lazy"
          style={{
            maxWidth: '100%',
            maxHeight: 288,
            borderRadius: 8,
            objectFit: 'contain',
            background: 'rgba(0,0,0,0.06)',
            display: 'block',
          }}
        />
        {content && <div style={captionStyle}>{content}</div>}
      </a>
    );
  }

  if (messageType === 'video') {
    return (
      <div>
        <video
          src={mediaUrl}
          controls
          preload="metadata"
          style={{ maxWidth: 320, width: '100%', borderRadius: 8, display: 'block' }}
        />
        {content && <div style={captionStyle}>{content}</div>}
      </div>
    );
  }

  if (messageType === 'audio') {
    return (
      <div>
        <audio
          src={mediaUrl}
          controls
          preload="metadata"
          style={{ width: '100%', minWidth: 220, display: 'block' }}
        />
        {content && <div style={captionStyle}>{content}</div>}
      </div>
    );
  }

  // document
  const filename = content || 'Documento';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: isVisitor ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
          color: isVisitor ? '#fff' : '#374151',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename}
        </div>
        <a
          href={mediaUrl}
          download
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 12, textDecoration: 'underline', opacity: 0.85, color: 'inherit' }}
        >
          Baixar
        </a>
      </div>
    </div>
  );
}
